// libs
import React, { useState, useEffect } from 'react';
import { Avatar, Button, Card, Col, Input, Modal, Row, Tag, Tooltip, message } from 'antd';
import { CopyOutlined, CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import styled from 'styled-components';

// components
import { StyledTable } from '../entity/shared/components/styled/StyledTable';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import { useTranslation } from '../../i18n/useTranslation';
import { DARK_BLACK } from '../entity/shared/constants';

// images
import { ReactComponent as IconDeleteOutline } from '../../images/icon-delete-outline.svg';
import { ReactComponent as IconArrowRange } from '../../images/icon-arrow-range.svg';
import { ReactComponent as IconSyncAlt } from '../../images/icon-sync-alt.svg';
import { ReactComponent as PlusOutlined } from '../../images/add-outline.svg';
import { ReactComponent as IconClose } from '../../images/icon-close.svg';
import { ReactComponent as IconEdit } from '../../images/icon-edit.svg';

// services
import { ENTITY_ACTIONS } from '../entity/shared/rbacConstantsService';
import { useRbac } from '../entity/shared/useRbac';

// types
import { SectionType, RelationRowData, OriginalRelationData, UpdateRelationPayload } from './types';

// generated
import { CustomRelationTypeInput, EntityType } from '../../types.generated';
import {
    useListCustomRelationTypesQuery,
    useCreateCustomRelationTypeMutation,
    useUpdateCustomRelationTypeMutation,
    useDeleteCustomRelationTypeMutation,
} from '../../graphql/glossary.generated';

// constants
import {
    TERM_TO_NODE,
    SYSTEM_TERM_TO_TERM_RELATIONS,
    SYSTEM,
    CUSTOM,
    SYSTEM_TOOLTIP,
    CUSTOM_TOOLTIP,
    ENTITY_TYPE_MAP,
    INITIAL_EMPTY_SYSTEM_ROW,
    SECTION_CONFIGS,
    ALL_SECTIONS,
    NEW_ROW_PREFIX,
    SYSTEM_ROW_PREFIX,
    isSystemType,
    PLACEHOLDER_TYPE_OF,
    PLACEHOLDER_HAS_TYPE,
    INFO_ICON_COLOR,
    RESERVED_RELATION_NAMES,
} from './constants';

const CardWrapper = styled(Card)`
    &.f-bordered {
        .ant-table-wrapper .ant-table table {
            .ant-table-row:not(:last-child) .ant-table-cell {
                border-bottom: 1px solid ${DARK_BLACK[1]};
            }
        }
    }

    .ant-table-wrapper .ant-table table {
        .ant-table-row .ant-table-cell {
            padding: 8px !important;
        }
    }
`;

interface Props {
    onClose: () => void;
}

const createSystemRow = (id: string, name: string, inverseName: string): RelationRowData => ({
    id,
    relation_type: SYSTEM,
    relation_type_of: name,
    icon: true,
    relation_has_type: inverseName,
    action: { tooltip: SYSTEM_TOOLTIP },
});

// Helper Functions
const getEntityTypes = (section: SectionType) => ENTITY_TYPE_MAP[section];

const normalizeEntityType = (type: string) => type?.toLowerCase();

const matchesSection = (sourceType: string, targetType: string, section: SectionType): boolean => {
    const { sourceEntityType, targetEntityType } = getEntityTypes(section);
    const normalizedSource = normalizeEntityType(sourceType);
    const normalizedTarget = normalizeEntityType(targetType);
    const expectedSource = normalizeEntityType(sourceEntityType);
    const expectedTarget = normalizeEntityType(targetEntityType);

    if (section === TERM_TO_NODE) {
        return (
            (normalizedSource === expectedSource && normalizedTarget === expectedTarget) ||
            (normalizedSource === expectedTarget && normalizedTarget === expectedSource)
        );
    }

    return normalizedSource === expectedSource && normalizedTarget === expectedTarget;
};

const hasRelationChanged = (original: OriginalRelationData, current: RelationRowData): boolean => {
    const currentName = current.relation_type_of?.trim() || '';
    const currentInverseName = current.relation_has_type?.trim() || '';
    const originalName = original.name?.trim() || '';
    const originalInverseName = original.inverseName?.trim() || '';
    return originalName !== currentName || originalInverseName !== currentInverseName;
};

const getNameAndInverseName = (row: RelationRowData): { name: string; inverseName: string } => {
    return {
        name: row.relation_type_of?.trim() || '',
        inverseName: row.relation_has_type?.trim() || '',
    };
};

const hasMatchingRelationNames = (row: RelationRowData): boolean => {
    const { name, inverseName } = getNameAndInverseName(row);
    // Only show error if both fields have values and they are the same
    return !!(name && inverseName && name.toLowerCase() === inverseName.toLowerCase());
};

const hasIncompleteRow = (row: RelationRowData): boolean => {
    const { name, inverseName } = getNameAndInverseName(row);
    // Return true if one field is filled but the other is empty
    return (!!name && !inverseName) || (!name && !!inverseName);
};

const isExistingRowEmptied = (row: RelationRowData): boolean => {
    // Check if this is an existing row (not new, not system) that has been completely emptied
    const isExistingCustomRow = !row.id.startsWith(NEW_ROW_PREFIX) && !row.id.startsWith(SYSTEM_ROW_PREFIX);
    const { name, inverseName } = getNameAndInverseName(row);
    // Return true if existing row has both fields empty
    return isExistingCustomRow && !name && !inverseName;
};

const isEmptyNewRow = (row: RelationRowData): boolean => {
    // Check if this is a new row that is completely empty
    const isNewRow = row.id.startsWith(NEW_ROW_PREFIX);
    const { name, inverseName } = getNameAndInverseName(row);
    return isNewRow && !name && !inverseName;
};

const hasReservedRelationName = (row: RelationRowData): boolean => {
    const { name, inverseName } = getNameAndInverseName(row);
    return RESERVED_RELATION_NAMES.has(name.toLowerCase()) || RESERVED_RELATION_NAMES.has(inverseName.toLowerCase());
};

export default function ManageRelationshipsModal(props: Props) {
    const { onClose } = props;
    const { t } = useTranslation();
    const { canPerformEntityMenuAction } = useRbac();
    const [isEditing, setIsEditing] = useState(false);
    const [copiedUrn, setCopiedUrn] = useState<string | null>(null);

    const canManageRelationships = canPerformEntityMenuAction(
        EntityType.GlossaryTerm,
        ENTITY_ACTIONS.MANAGE_CUSTOM_RELATIONSHIP,
    );
    const canCreateRelationship =
        canManageRelationships ||
        canPerformEntityMenuAction(EntityType.GlossaryTerm, ENTITY_ACTIONS.CREATE_CUSTOM_RELATIONSHIP);
    const canUpdateRelationship =
        canManageRelationships ||
        canPerformEntityMenuAction(EntityType.GlossaryTerm, ENTITY_ACTIONS.UPDATE_CUSTOM_RELATIONSHIP);
    const canDeleteRelationship =
        canManageRelationships ||
        canPerformEntityMenuAction(EntityType.GlossaryTerm, ENTITY_ACTIONS.DELETE_CUSTOM_RELATIONSHIP);
    const canEditRelationships = canCreateRelationship || canUpdateRelationship || canDeleteRelationship;

    const tooltipEditRelationships = !canEditRelationships ? t('UNAUTHORISED_TOOLTIP') : undefined;
    const tooltipCreateRelationship = !canCreateRelationship ? t('UNAUTHORISED_TOOLTIP') : undefined;
    const tooltipDeleteRelationship = !canDeleteRelationship ? t('UNAUTHORISED_TOOLTIP') : undefined;

    const handleCopyUrn = (urn: string) => {
        navigator.clipboard?.writeText(urn);
        setCopiedUrn(urn);
        setTimeout(() => setCopiedUrn(null), 2000);
    };

    const {
        data: customRelationTypesData,
        loading,
        refetch,
    } = useListCustomRelationTypesQuery({
        fetchPolicy: 'no-cache',
    });
    const [createCustomRelationType, { loading: createLoading }] = useCreateCustomRelationTypeMutation();
    const [updateCustomRelationType, { loading: updateLoading }] = useUpdateCustomRelationTypeMutation();
    const [deleteCustomRelationType, { loading: deleteLoading }] = useDeleteCustomRelationTypeMutation();

    // Track original data to detect changes
    const [originalCustomRelations, setOriginalCustomRelations] = useState<Record<string, OriginalRelationData>>({});
    // Track deleted URNs
    const [deletedUrns, setDeletedUrns] = useState<Set<string>>(new Set());

    // Initialize system rows
    const [termsToTermsData, setTermsToTermsData] = useState<RelationRowData[]>(() =>
        SYSTEM_TERM_TO_TERM_RELATIONS.map((rel, index) =>
            createSystemRow(`system-term-${index}`, rel.name, rel.inverseName),
        ),
    );

    // Initialize terms to nodes data
    const [termsToNodesData, setTermsToNodesData] = useState<RelationRowData[]>([
        { ...INITIAL_EMPTY_SYSTEM_ROW, id: 'system-term-node-1' },
    ]);

    // Initialize nodes to nodes data
    const [nodesToNodesData, setNodesToNodesData] = useState<RelationRowData[]>([
        { ...INITIAL_EMPTY_SYSTEM_ROW, id: 'system-node-node-1' },
    ]);

    // Helper to create RelationRowData from original data
    const createRelationRowData = (urn: string, name: string, inverseName: string): RelationRowData => ({
        id: urn,
        relation_type: CUSTOM,
        relation_type_of: name,
        icon: false,
        relation_has_type: inverseName,
        action: { tooltip: CUSTOM_TOOLTIP },
    });

    // Helper to categorize relations by section
    const categorizeRelations = (
        relationsData: Array<{
            urn: string;
            name: string;
            inverseName: string;
            sourceEntityType: string;
            targetEntityType: string;
        }>,
    ): Record<SectionType, RelationRowData[]> => {
        const categorized: Record<SectionType, RelationRowData[]> = {
            termsToTerms: [],
            termsToNodes: [],
            nodesToNodes: [],
        };

        relationsData.forEach(({ urn, name, inverseName, sourceEntityType, targetEntityType }) => {
            const relationData = createRelationRowData(urn, name, inverseName);
            const section = ALL_SECTIONS.find((s) => matchesSection(sourceEntityType, targetEntityType, s));

            if (section) {
                categorized[section].push(relationData);
            }
        });

        return categorized;
    };

    /**
     * This method merges the current rows with the API rows.
     * 1. It creates a map of the API rows by their ID.
     * 2. It filters the current rows to only include custom rows (not system or new rows).
     * 3. It creates a set of the existing custom row IDs.
     * 4. It filters the existing custom rows to only include rows that exist in the API rows.
     * 5. It filters the API rows to only include rows that do not exist in the existing custom rows.
     */
    const mergeRelations = (currentRows: RelationRowData[], apiRows: RelationRowData[]): RelationRowData[] => {
        const apiRowMap = new Map(apiRows.map((row) => [row.id, row]));
        const existingCustomRows = currentRows.filter(
            (row) => row.relation_type !== SYSTEM && !row.id.startsWith(NEW_ROW_PREFIX),
        );
        const existingCustomIds = new Set(existingCustomRows.map((row) => row.id));
        const preserved = existingCustomRows
            .filter((row) => apiRowMap.has(row.id))
            .map((row) => apiRowMap.get(row.id)!);
        const brandNew = apiRows.filter((row) => !existingCustomIds.has(row.id));
        return [...currentRows.filter((row) => row.relation_type === SYSTEM), ...preserved, ...brandNew];
    };

    // Helper to update all section states with categorized relations
    const updateAllSectionStates = (categorizedRelations: Record<SectionType, RelationRowData[]>) => {
        setTermsToTermsData((prev) => mergeRelations(prev, categorizedRelations.termsToTerms));
        setTermsToNodesData((prev) => mergeRelations(prev, categorizedRelations.termsToNodes));
        setNodesToNodesData((prev) => mergeRelations(prev, categorizedRelations.nodesToNodes));
    };

    // Process custom relation types when data is loaded
    useEffect(() => {
        const relations = customRelationTypesData?.listCustomRelationTypes?.customRelations;
        if (!relations) return;

        const originalData: Record<string, OriginalRelationData> = {};
        const relationsData = relations.map((relation) => {
            originalData[relation.urn] = {
                name: relation.name || '',
                inverseName: relation.inverseName || '',
                sourceEntityType: relation.sourceEntityType,
                targetEntityType: relation.targetEntityType,
            };
            return {
                urn: relation.urn,
                name: relation.name || '',
                inverseName: relation.inverseName || '',
                sourceEntityType: relation.sourceEntityType,
                targetEntityType: relation.targetEntityType,
            };
        });

        setOriginalCustomRelations(originalData);
        updateAllSectionStates(categorizeRelations(relationsData));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customRelationTypesData]);

    // Check if any row has validation errors (relation name equals inverse name)
    const hasValidationErrors = (): boolean => {
        const allData = [...termsToTermsData, ...termsToNodesData, ...nodesToNodesData];
        return allData.some((row) => row.relation_type === CUSTOM && hasMatchingRelationNames(row));
    };

    // Check if any row has incomplete data (one field filled, other empty), existing row emptied, or empty new row
    const hasIncompleteRows = (): boolean => {
        const allData = [...termsToTermsData, ...termsToNodesData, ...nodesToNodesData];
        return allData.some(
            (row) =>
                row.relation_type === CUSTOM &&
                (hasIncompleteRow(row) || isExistingRowEmptied(row) || isEmptyNewRow(row)),
        );
    };

    // Check if any custom row uses reserved system relation names
    const hasReservedNames = (): boolean => {
        const allData = [...termsToTermsData, ...termsToNodesData, ...nodesToNodesData];
        return allData.some((row) => row.relation_type === CUSTOM && hasReservedRelationName(row));
    };

    // Helper to get/set data for a specific section
    const getSectionData = (section: SectionType): RelationRowData[] => {
        const dataMap = {
            termsToTerms: termsToTermsData,
            termsToNodes: termsToNodesData,
            nodesToNodes: nodesToNodesData,
        };
        return dataMap[section];
    };

    const setSectionData = (
        section: SectionType,
        data: RelationRowData[] | ((prev: RelationRowData[]) => RelationRowData[]),
    ) => {
        const setterMap = {
            termsToTerms: setTermsToTermsData,
            termsToNodes: setTermsToNodesData,
            nodesToNodes: setNodesToNodesData,
        };
        setterMap[section](data);
    };

    // Function to add new row
    const addNewRow = (section: SectionType) => {
        const newRow: RelationRowData = {
            id: `${NEW_ROW_PREFIX}${Date.now()}`,
            relation_type: CUSTOM,
            relation_type_of: '',
            icon: false,
            relation_has_type: '',
            action: { tooltip: CUSTOM_TOOLTIP },
        };
        setSectionData(section, [...getSectionData(section), newRow]);
    };

    // Function to delete a row
    const deleteRow = (section: SectionType, rowId: string) => {
        // If it's an existing custom relation (has a URN), track it for deletion
        if (!rowId.startsWith(NEW_ROW_PREFIX) && !rowId.startsWith(SYSTEM_ROW_PREFIX)) {
            setDeletedUrns((prev) => new Set(prev).add(rowId));
        }

        // Remove from UI
        setSectionData(
            section,
            getSectionData(section).filter((row) => row.id !== rowId),
        );
    };

    // Function to show delete confirmation for existing custom relationships only
    const confirmDeleteRow = (section: SectionType, rowId: string) => {
        // For new entries (not yet saved), delete directly without confirmation
        if (rowId.startsWith(NEW_ROW_PREFIX)) {
            deleteRow(section, rowId);
            return;
        }

        // For existing entries, show confirmation modal
        Modal.confirm({
            centered: true,
            title: t('Delete Relationship'),
            content: t('DELETE_RELATIONSHIP_CONFIRMATION'),
            okText: t('Delete'),
            cancelText: t('Cancel'),
            okButtonProps: { danger: true },
            onOk: () => deleteRow(section, rowId),
        });
    };

    // Helper to restore a failed row back to the UI
    const restoreFailedRow = (urn: string, originalData: OriginalRelationData) => {
        const restoredRow: RelationRowData = {
            id: urn,
            relation_type: CUSTOM,
            relation_type_of: originalData.name,
            icon: false,
            relation_has_type: originalData.inverseName,
            action: { tooltip: CUSTOM_TOOLTIP },
        };

        const section = ALL_SECTIONS.find((s) =>
            matchesSection(originalData.sourceEntityType, originalData.targetEntityType, s),
        );

        if (section) {
            setSectionData(section, [...getSectionData(section), restoredRow]);
        }
    };

    // Function to reset all section data to original state
    const resetAllSectionData = () => {
        const relationsData = Object.entries(originalCustomRelations).map(([urn, original]) => ({
            urn,
            name: original.name,
            inverseName: original.inverseName,
            sourceEntityType: original.sourceEntityType,
            targetEntityType: original.targetEntityType,
        }));
        updateAllSectionStates(categorizeRelations(relationsData));
    };

    // Collect relations to create/update
    const collectRelationChanges = () => {
        const newRelations: CustomRelationTypeInput[] = [];
        const newRelationRowIds: string[] = []; // Track row IDs for new relations
        const updatedRelations: UpdateRelationPayload[] = [];

        ALL_SECTIONS.forEach((section) => {
            const data = getSectionData(section);
            const { sourceEntityType, targetEntityType } = getEntityTypes(section);

            data.forEach((row) => {
                if (row.relation_type !== CUSTOM || !row.relation_type_of || !row.relation_has_type) return;

                const relationPayload = {
                    name: row.relation_type_of,
                    inverseName: row.relation_has_type,
                    sourceEntityType,
                    targetEntityType,
                };

                if (row.id.startsWith(NEW_ROW_PREFIX)) {
                    newRelations.push(relationPayload);
                    newRelationRowIds.push(row.id); // Store the row ID
                } else {
                    const original = originalCustomRelations[row.id];
                    if (original && hasRelationChanged(original, row)) {
                        updatedRelations.push({ urn: row.id, ...relationPayload });
                    }
                }
            });
        });

        return { newRelations, newRelationRowIds, updatedRelations };
    };

    const setCustomRelationData = (updatedUrns?: { oldRowId: string; newUrn: string }[]) => {
        // If updatedUrns is provided, update the row IDs in the section data
        if (updatedUrns && updatedUrns.length > 0) {
            // Create a map for quick lookup
            const urnMap = new Map(updatedUrns.map((item) => [item.oldRowId, item.newUrn]));

            // Also update the originalCustomRelations with the new URNs
            const updatedOriginalRelations = { ...originalCustomRelations };

            // Update each section's data by replacing old row IDs with new URNs
            ALL_SECTIONS.forEach((section) => {
                const { sourceEntityType, targetEntityType } = getEntityTypes(section);
                setSectionData(section, (prevData) =>
                    prevData.map((row) => {
                        const newUrn = urnMap.get(row.id);
                        if (newUrn) {
                            // Update our "Source of Truth" object
                            updatedOriginalRelations[newUrn] = {
                                name: row.relation_type_of || '',
                                inverseName: row.relation_has_type || '',
                                sourceEntityType,
                                targetEntityType,
                            };
                            // Return the updated row with the real URN
                            return { ...row, id: newUrn };
                        }
                        return row;
                    }),
                );
            });

            setOriginalCustomRelations(updatedOriginalRelations);
        }
    };

    // Function to handle save
    const handleSave = async () => {
        // Check for validation errors before saving
        if (hasValidationErrors()) {
            message.error(t('Relation name and inverse name cannot be the same.'));
            return;
        }

        // Check for incomplete or empty rows
        if (hasIncompleteRows()) {
            message.error(t('CUSTOM_RELATION_INCOMPLETE_ROWS'));
            return;
        }

        // Check for reserved system relation names
        if (hasReservedNames()) {
            message.error(t('Custom relationships cannot use reserved system names.'));
            return;
        }

        try {
            const { newRelations, newRelationRowIds, updatedRelations } = collectRelationChanges();
            const urnsToDelete = Array.from(deletedUrns);

            if (newRelations.length === 0 && updatedRelations.length === 0 && urnsToDelete.length === 0) {
                message.info(t('No changes to save.'));
                setIsEditing(false);
                return;
            }

            let createSuccess = false;
            let deletedCount = 0;

            // Create (single batched call)
            if (newRelations.length > 0) {
                const response = await createCustomRelationType({
                    variables: { input: { customRelationTypes: newRelations } },
                });
                if (response.errors) {
                    message.error(
                        t('Failed to create relationships: {{message}}', { message: response.errors?.[0]?.message }),
                    );
                    return;
                }
                createSuccess = true;

                // Map old temporary row IDs to new URNs returned from the backend
                const urnsToUpdate = newRelationRowIds.map((rowId, index) => ({
                    oldRowId: rowId,
                    newUrn: response.data?.createCustomRelationType?.[index] || '',
                }));

                // Update the row IDs in the UI state immediately
                setCustomRelationData(urnsToUpdate);
            }

            // Delete (single batched call)
            if (urnsToDelete.length > 0) {
                const deleteResult = await deleteCustomRelationType({
                    variables: { input: { urns: urnsToDelete } },
                });

                // Check delete results for errors and restore failed items
                const deleteErrors = deleteResult?.data?.deleteCustomRelationType?.errors;
                if (deleteErrors && deleteErrors.length > 0) {
                    const firstError = deleteErrors[0];
                    // Restore the failed item back to the UI
                    const originalData = originalCustomRelations[firstError.urn];
                    if (originalData) {
                        restoreFailedRow(firstError.urn, originalData);
                    }
                } else if (deleteResult?.data?.deleteCustomRelationType?.success) {
                    deletedCount = deleteResult.data.deleteCustomRelationType.deletedUrns?.length || 0;
                }
            }

            // Updates (use allSettled to process all and handle partial failures)
            const updateResults = await Promise.allSettled(
                updatedRelations.map((relation) => updateCustomRelationType({ variables: { input: relation } })),
            );

            const successfulUpdates = updateResults.filter((result) => result.status === 'fulfilled').length;

            // Success message
            const successMessages: string[] = [];

            if (createSuccess && newRelations.length > 0) {
                successMessages.push(t('{{count}} relationship(s) created', { count: newRelations.length }));
            }

            if (successfulUpdates > 0) {
                successMessages.push(t('{{count}} relationship(s) updated', { count: successfulUpdates }));
            }

            if (deletedCount > 0) {
                successMessages.push(t('{{count}} relationship(s) deleted', { count: deletedCount }));
            }

            // Show success messages if any
            if (successMessages.length > 0) {
                message.success(successMessages.join(', '));
            }

            // Clear deleted URNs tracking
            setDeletedUrns(new Set());
            await refetch();
            setIsEditing(false);
        } catch (err) {
            console.error('Failed to save changes:', err);
        }
    };

    // Function to update input values
    const updateRowValue = (
        section: SectionType,
        rowId: string,
        field: keyof Pick<RelationRowData, 'relation_type_of' | 'relation_has_type'>,
        value: string,
    ) => {
        setSectionData(
            section,
            getSectionData(section).map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
        );
    };

    // Helper to render input column
    const renderInputColumn = (
        field: 'relation_type_of' | 'relation_has_type',
        placeholder: string,
        section: SectionType,
    ) => ({
        dataIndex: field,
        key: field,
        render: (_: string, record: RelationRowData) => {
            const isNewRow = record.id.startsWith(NEW_ROW_PREFIX);
            const canEditThisRow = isNewRow ? canCreateRelationship : canUpdateRelationship;
            return (
                <Input
                    size="middle"
                    maxLength={300}
                    title={record[field]}
                    value={record[field]}
                    onChange={(e) => updateRowValue(section, record.id, field, e.target.value)}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    className="f-alternate-bg"
                    disabled={isSystemType(record.relation_type) || !isEditing || !canEditThisRow}
                />
            );
        },
    });

    // Function to create grid columns for a specific section
    const createGridColumns = (section: SectionType): ColumnsType<RelationRowData> => {
        const columns: ColumnsType<RelationRowData> = [
            {
                width: '10%',
                dataIndex: 'relation_type',
                key: 'relation_type',
                render: (value: string) => (
                    <Tag className={`label-tag ${value === SYSTEM ? 'base-tag' : ''} ma-0`}>{value}</Tag>
                ),
            },
            renderInputColumn('relation_type_of', PLACEHOLDER_TYPE_OF, section),
            {
                width: '40px',
                dataIndex: 'icon',
                key: 'icon',
                render: () => <Avatar icon={<IconArrowRange />} className="f-avatar-small f-avatar-transparent" />,
            },
            renderInputColumn('relation_has_type', PLACEHOLDER_HAS_TYPE, section),
        ];

        // Show delete column when in edit mode, copy URN column when not editing (for custom relations only)
        if (isEditing) {
            columns.push({
                width: '80px',
                dataIndex: 'action',
                key: 'action',
                render: (_: { tooltip: string }, record: RelationRowData) => {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Tooltip
                                title={
                                    isSystemType(record.relation_type)
                                        ? t(record?.action?.tooltip || '')
                                        : tooltipDeleteRelationship
                                }
                                overlayClassName="f-tooltip-white"
                            >
                                <Button
                                    type="text"
                                    className="f-btn-flat"
                                    icon={<IconDeleteOutline />}
                                    title={t('Delete Relationship')}
                                    aria-label={t('Delete Relationship')}
                                    onClick={() => confirmDeleteRow(section, record.id)}
                                    disabled={isSystemType(record.relation_type) || !canDeleteRelationship}
                                />
                            </Tooltip>
                            {!isSystemType(record.relation_type) && (
                                <Tooltip
                                    title={t(
                                        'Define the forward relationship on the left and the corresponding inverse (reciprocal) relationship on the right.',
                                    )}
                                    overlayClassName="f-tooltip-white"
                                >
                                    <InfoCircleOutlined style={{ color: INFO_ICON_COLOR, cursor: 'pointer' }} />
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            });
        } else {
            // Show copy URN button for custom relations when not editing
            columns.push({
                width: '48px',
                dataIndex: 'action',
                key: 'action',
                render: (_: { tooltip: string }, record: RelationRowData) => {
                    const isCustomType = record.relation_type === CUSTOM;
                    // Only show copy button for custom relations (they have URNs)
                    if (!isCustomType) return null;

                    const isCopied = copiedUrn === record.id;
                    return (
                        <Tooltip
                            title={t('Copy URN. An URN uniquely identifies an entity on Data Catalog.')}
                            overlayClassName="f-tooltip-white"
                        >
                            <Button
                                type="text"
                                className="f-btn-flat"
                                icon={isCopied ? <CheckOutlined /> : <CopyOutlined />}
                                title={t('Copy URN')}
                                aria-label={t('Copy URN')}
                                onClick={() => handleCopyUrn(record.id)}
                            />
                        </Tooltip>
                    );
                },
            });
        }

        return columns;
    };

    // Helper function to render a relationship section
    const renderRelationshipSection = (
        section: SectionType,
        sourceLabel: string,
        targetLabel: string,
        description: string,
        data: RelationRowData[],
        isFirstSection = false,
    ) => (
        <React.Fragment key={section}>
            <Row align="middle" gutter={8} className={`${isFirstSection ? 'mb-2' : 'mt-4 mb-2'}`}>
                <Col flex="none">
                    <div className="f-text-big-content f-text-single-line f-text-semibold f-color-dark-black-s100">
                        {t(sourceLabel)}
                    </div>
                </Col>
                <Col flex="none">
                    <Avatar icon={<IconSyncAlt />} className="f-avatar-small f-avatar-transparent" />
                </Col>
                <Col flex="none">
                    <div className="f-text-big-content f-text-single-line f-text-semibold f-color-dark-black-s100">
                        {t(targetLabel)}
                    </div>
                </Col>
            </Row>
            <div className="f-text-medium-content f-color-dark-black-s70 mb-3">{t(description)}</div>

            <CardWrapper className={`${isEditing ? '' : 'f-bordered'}`}>
                <StyledTable
                    rowKey="id"
                    size="small"
                    showHeader={false}
                    pagination={false}
                    dataSource={data}
                    // typescript is complaining that default sort order is not a valid column field- overriding this here
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    columns={createGridColumns(section)}
                />
            </CardWrapper>
            {isEditing && (
                <Tooltip title={tooltipCreateRelationship} overlayClassName="f-tooltip-white">
                    <Button
                        block
                        type="default"
                        className="mt-4"
                        onClick={() => addNewRow(section)}
                        disabled={!canCreateRelationship}
                    >
                        <PlusOutlined aria-hidden="true" />
                        <span>{t('Add')}</span>
                    </Button>
                </Tooltip>
            )}
        </React.Fragment>
    );

    return (
        <Modal
            open
            centered
            closable={false}
            className="f-modal"
            wrapClassName="f-modal-vertical-scroll"
            title={
                <>
                    {loading && <ComponentSpinner />}
                    <div className="f-modal-header">
                        <h1 className="f-modal-title">{t('Manage Relationships')}</h1>
                        <div className="f-modal-text">
                            {t('Listed below are the relationships that are defined by the system and the user.')}
                        </div>
                    </div>
                    <div className="f-modal-actions">
                        {!isEditing && (
                            <Tooltip title={tooltipEditRelationships} overlayClassName="f-tooltip-white">
                                <Button
                                    type="default"
                                    onClick={() => setIsEditing(true)}
                                    disabled={!canEditRelationships}
                                >
                                    <IconEdit />
                                    <span className="mt-1">{t('Edit')}</span>
                                </Button>
                            </Tooltip>
                        )}
                        <Button
                            type="default"
                            onClick={onClose}
                            title={t('Close Modal')}
                            icon={<IconClose />}
                            aria-label={t('Close Modal')}
                        />
                    </div>
                </>
            }
            footer={
                isEditing ? (
                    <>
                        <Button
                            type="default"
                            onClick={() => {
                                setIsEditing(false);
                                setDeletedUrns(new Set());
                                resetAllSectionData();
                                refetch();
                            }}
                            disabled={createLoading || updateLoading || deleteLoading}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSave}
                            loading={createLoading || updateLoading || deleteLoading}
                        >
                            {t('Save')}
                        </Button>
                    </>
                ) : (
                    false
                )
            }
        >
            {SECTION_CONFIGS.map((config, index) =>
                renderRelationshipSection(
                    config.section,
                    config.sourceLabel,
                    config.targetLabel,
                    config.description,
                    getSectionData(config.section),
                    index === 0,
                ),
            )}
        </Modal>
    );
}
